<template>
  <div
    data-locator="global-search"
    class="GlobalSearch px-4"
    :class="[
      `GlobalSearch--${size}`,
      {
        'GlobalSearch--disabled': disabled,
        'GlobalSearch--filled': filled,
        'GlobalSearch--contrast': contrast,
      },
    ]"
  >
    <!-- Search input -->
    <AppTextField
      v-model="model"
      class="GlobalSearch__input flex-grow-1"
      :placeholder="placeholder"
      :disabled="disabled"
      :clearable="!disabled && !loading"
      density="compact"
      variant="outlined"
      clear-icon="close"
      @focus="() => emit('focus')"
      @keydown.enter.prevent="() => emit('enter')"
    />

    <app-material-icon name="search" :color="computedColor" />

    <!-- Filter button -->
    <app-button
      v-if="showFilterIcon"
      type="icon"
      icon
      size="small"
      :disabled="disabled"
      data-locator="global-search-filter-button"
      @click="onClickFilter"
    >
      <app-material-icon name="instant_mix" size="sm" :color="computedColor" />
    </app-button>

    <!-- Scope Options -->
    <template v-if="showScope">
      <div class="GlobalSearch__divider mx-2" />

      <v-menu v-model="menuOpen" location="bottom">
        <template #activator="{ props }">
          <app-button
            class="pa-1"
            size="medium"
            data-locator="global-search-scope-selector"
            :disabled="disabled"
            v-bind="props"
          >
            <span :class="disabled ? 'text-grey-200' : 'text-grey-darken-3'">
              {{ currentScopeLabel }}
              <app-material-icon name="keyboard_arrow_down" size="sm" class="ml-1" :color="computedColor" />
            </span>
          </app-button>
        </template>

        <v-list
          class="GlobalSearch__menu"
          density="compact"
          data-locator="global-search-scope-menu"
        >
          <v-list-item
            v-for="option in scopeOptions"
            :key="option.value"
            :data-locator="`global-search-scope-item-${option.value}`"
            @click="selectScope(option)"
          >
            <v-list-item-title>{{ option.label }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

import AppTextField from '@/common/AppTextField.vue'

interface ScopeOption {
  value: string
  label: string
}

const { t } = useI18n()

const emit = defineEmits<{
  (e: 'update:modelValue', val: string): void
  (e: 'click:filter'): void
  (e: 'click:scope', option: ScopeOption): void
  (e: 'focus'): void
  (e: 'enter'): void
}>()

const props = withDefaults(defineProps<{
  modelValue?: string | null
  size?: 'medium' | 'large'
  disabled?: boolean
  filled?: boolean
  contrast?: boolean
  showFilterIcon?: boolean
  showScope?: boolean
  loading?: boolean
  placeholder?: string
}>(), {
  modelValue: '',
  size: 'medium',
  disabled: false,
  filled: false,
  contrast: false,
  showFilterIcon: false,
  showScope: false,
  loading: false,
  placeholder: '',
})

const model = computed<string>({
  get: () => props.modelValue ?? '',
  set: (val) => emit('update:modelValue', val),
})

const menuOpen = ref(false)

const selectedScope = ref<string>('closd')

const scopeOptions: ScopeOption[] = [
  { value: 'closd', label: t('common.scopeClosd') },
  { value: 'project', label: t('common.scopeProject') },
]

const currentScopeLabel = computed(() => {
  return scopeOptions.find(opt => opt.value === selectedScope.value)?.label || ''
})

const computedColor = computed(() => (props.disabled ? 'grey-200' : undefined))

const onClickFilter = () => emit('click:filter')

function selectScope(option: ScopeOption) {
  selectedScope.value = option.value
  emit('click:scope', option)
  menuOpen.value = false
}
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";

.GlobalSearch {
  display: flex;
  align-items: center;
  max-width: 680px;
  min-width: 160px;
  @include border-radius('border-radius-pill');
  outline: 1px solid rgb(var(--v-theme-border-secondary));
  background-color: transparent;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:not(.GlobalSearch--disabled):hover {
    background-color: #0B0C0D0D;
    outline: 1px solid rgb(var(--v-theme-border-secondary-hover));
  }

  &:not(.GlobalSearch--disabled):focus-within {
    outline: 2px solid rgb(var(--v-theme-border-info-bold));
  }

  &--medium {
    height: 36px;
  }

  &--large {
    height: 48px;
  }

  &--disabled {
    background-color: rgb(var(--v-theme-surface-disabled));
    outline: 1px solid rgb(var(--v-theme-border-disabled));
    pointer-events: none;
  }

  &--filled {
    background-color: rgb(var(--v-theme-grey-25));
  }

  &--contrast {
    outline-color: #000;
  }

  &__input {
    flex: 1;

    :deep(.v-field) {
      padding: 0;
      margin: 0;
      background-color: transparent;
    }

    :deep(.v-field__input) {
      padding: 0 0 0 4px;
      font-size: 14px;
      line-height: 20px;
    }

    :deep(.v-field__outline) {
      display: none;
    }
  }

  &__divider {
    height: 24px;
    width: 1px;
    background-color: rgb(var(--v-theme-grey-200));
  }
}
</style>
